import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, RefreshCw, ExternalLink, Mail, Globe, Phone, Send, Calendar, Copy, Check } from "lucide-react";
import { api } from "../../lib/api";

const INDEX_LABELS = {
  investor: "Инвесторский интерес",
  corporate: "Корпоративные пилоты",
  institutional: "Институциональная поддержка",
};

const AI_STATUS_LABELS = {
  ok: "Применено",
  skipped: "Пропущено (нет открытых ответов)",
  no_key: "Недоступно (ключ не настроен)",
  api_error: "Ошибка API провайдера",
  error: "Внутренняя ошибка",
  extraction_failed: "Парсинг неудачен",
};

const CONFIDENCE_LABELS = {
  normal: "Высокая",
  medium: "Средняя",
  low: "Низкая",
};

const COMPONENT_LABELS = {
  // investor
  stage_product: "Стадия и продукт",
  revenue_growth: "Выручка и рост",
  customers_sales: "Клиенты и продажи",
  market_scalability: "Рынок и масштабируемость",
  team: "Команда",
  investor_readiness: "Инвестиционная готовность",
  ip_barriers: "IP и барьеры",
  risk_reduction: "Снижение рисков",
  deck_quality: "Качество презентации",
  // corporate
  business_value: "Бизнес-ценность",
  pilots_adoption: "Готовность к пилоту",
  measurable_effect: "Измеримый эффект",
  integration_readiness: "Готовность к интеграции",
  b2b_b2g_fit: "Соответствие B2B / B2G",
  industry_fit: "Отраслевое соответствие",
  team_delivery: "Команда поставки",
  implementation_risk: "Риск внедрения",
  // institutional
  tech_novelty: "Технологическая новизна",
  priority_alignment: "Соответствие приоритетам",
  rnd_ip_science: "НИОКР и научная база",
  grant_readiness: "Готовность к грантам",
  impact: "Социальный эффект",
  feasibility_stage: "Реализуемость и стадия",
  team_partners: "Команда и партнёры",
  documentation: "Документация",
};

const DECK_FIELD_LABELS = [
  ["summary", "Резюме"],
  ["problem", "Проблема"],
  ["solution", "Решение"],
  ["icp", "ICP"],
  ["market_size", "Рынок"],
  ["business_model", "Бизнес-модель"],
  ["traction_signals", "Traction"],
  ["team", "Команда"],
  ["ask", "Раунд"],
  ["competition", "Конкуренты"],
  ["moat", "Moat"],
  ["financials_signals", "Финансы"],
  ["deck_quality_notes", "Заметки о качестве"],
];

function fmtDate(iso) {
  return iso ? iso.slice(0, 16).replace("T", " ") : "—";
}

function answerToLabel(question, value) {
  if (value === undefined || value === null || value === "") return null;
  if (Array.isArray(value)) {
    const labels = value.map((v) => {
      const opt = (question?.options || []).find((o) => o.value === v);
      return opt ? opt.label : String(v);
    });
    return labels.join(", ");
  }
  const opt = (question?.options || []).find((o) => o.value === value);
  return opt ? opt.label : String(value);
}

export default function SubmissionDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [recomp, setRecomp] = useState({ profile_id: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => api.get(`/api/admin/submissions/${id}`).then((r) => setData(r.data)),
    [id]
  );

  useEffect(() => {
    load();
    api.get("/api/admin/scoring-profiles").then((r) => setProfiles(r.data));
  }, [load]);

  useEffect(() => {
    if (!data?.submission) return;
    const t = data.submission.questionnaire_type || "mvp";
    api.get(`/api/questionnaire?type=${t}`).then((r) => setQuestionnaire(r.data));
  }, [data]);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post(`/api/admin/submissions/${id}/recompute`, { profile_id: recomp.profile_id || null });
      await load();
    } finally { setBusy(false); }
  };

  const reanalyzeDeck = async () => {
    setBusy(true);
    try {
      await api.post(`/api/submissions/${id}/deck/reanalyze`);
      await load();
    } finally { setBusy(false); }
  };

  const openDeck = async () => {
    const r = await api.get(`/api/admin/submissions/${id}/deck/download`);
    window.open(r.data.download_url, "_blank", "noreferrer");
  };

  const [emdStatus, setEmdStatus] = useState(null); // null | "busy" | {ok} | {error}
  const exportToEmd = async () => {
    if (!window.confirm("Выгрузить сабмит в EMD Cloud (Hop Agency)?\nСоздаст новую row в коллекции стартапов.")) return;
    setEmdStatus("busy");
    try {
      await api.post(`/api/admin/submissions/${id}/export-to-emd`);
      setEmdStatus({ ok: true });
    } catch (e) {
      setEmdStatus({ error: e.response?.data?.detail || e.message });
    }
  };

  const sectionedAnswers = useMemo(() => {
    if (!questionnaire || !data?.submission) return [];
    const answers = data.submission.raw_answers_json || {};
    const bySection = new Map();
    for (const q of questionnaire.questions || []) {
      const key = q.section || "Прочее";
      if (!bySection.has(key)) bySection.set(key, []);
      const value = answers[q.id];
      bySection.get(key).push({ q, value, label: answerToLabel(q, value) });
    }
    return Array.from(bySection, ([section, items]) => ({ section, items }));
  }, [questionnaire, data]);

  if (!data) return <div className="label-mono">Загрузка…</div>;
  const { submission, score, recommendation, extraction, verification } = data;
  const f = submission.founder || {};
  const deck = submission.deck || null;
  const deckParsed = deck?.extraction?.parsed || {};
  const consent = submission.consent || {};

  return (
    <div className="space-y-12 print:space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 print:hidden">
        <Link to="/admin" className="hop-link text-sm">← к списку сабмитов</Link>
        <button onClick={() => window.print()} className="btn-ghost text-xs" data-testid="print-card-btn">
          Распечатать карточку
        </button>
      </div>

      <div>
        <div className="label-mono">КАРТОЧКА СТАРТАПА</div>
        <h1 className="mt-3 text-4xl md:text-5xl font-medium tracking-tight">{f.startup_name}</h1>
        {f.legal_entity_name && (
          <div className="mt-1 text-sm text-foreground/60 mono" data-testid="founder-legal-name">{f.legal_entity_name}</div>
        )}
        <div className="mt-4 grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-foreground/85">
          {f.website && (
            <ContactRow icon={<Globe size={14} />} label="Сайт">
              <a href={f.website.startsWith("http") ? f.website : `https://${f.website}`} target="_blank" rel="noreferrer" className="hop-link">
                {f.website} <ExternalLink size={11} className="inline" />
              </a>
            </ContactRow>
          )}
          {f.email && (
            <ContactRow icon={<Mail size={14} />} label="E-mail">
              <a href={`mailto:${f.email}`} className="hop-link">{f.email}</a>
            </ContactRow>
          )}
          {f.founder_name && <ContactRow icon={null} label="Контактное лицо">{f.founder_name}</ContactRow>}
          {f.telegram && <ContactRow icon={<Send size={14} />} label="Telegram">{f.telegram}</ContactRow>}
          {f.phone && <ContactRow icon={<Phone size={14} />} label="Телефон">{f.phone}</ContactRow>}
          {f.city && <ContactRow icon={null} label="Город">{f.city}</ContactRow>}
          {f.technologies?.length > 0 && <ContactRow icon={null} label="Технологии">{(f.technology_labels || f.technologies).join(", ")}</ContactRow>}
          <ContactRow icon={<Calendar size={14} />} label="Подано">{fmtDate(submission.created_at)}</ContactRow>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs label-mono text-foreground/60">
          <span className="border border-foreground/15 px-2 py-0.5">
            ANKETA · {(submission.questionnaire_type || "mvp").toUpperCase()} · {submission.questionnaire_version || ""}
          </span>
          <span className="border border-foreground/15 px-2 py-0.5">ID · {submission.id.slice(0, 8)}</span>
          {consent.privacy_policy_accepted && <span className="border border-foreground/15 px-2 py-0.5">152-FZ ✓</span>}
          {consent.ai_processing_accepted && <span className="border border-foreground/15 px-2 py-0.5">AI ✓</span>}
        </div>
      </div>

      {/* Public share link — same URL as founder receives */}
      {submission.share_token && (
        <PublicShareBlock shareToken={submission.share_token} />
      )}

      {/* Indices */}
      {score ? (
        <div>
          <div className="label-mono mb-4">ИНДЕКСЫ</div>
          <div className="grid md:grid-cols-3 border border-black/10">
            {["investor", "corporate", "institutional"].map((k) => (
              <div key={k} className="p-6 border-r border-black/10 last:border-r-0 last:border-b-0 border-b md:border-b-0">
                <div className="label-mono">{INDEX_LABELS[k]}</div>
                <div className="mono text-5xl mt-3" data-testid={`index-${k}-value`}>{score.indices[k].value}</div>
                <div className="text-xs text-foreground/70 mt-2">{score.indices[k].band.label}</div>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-4 text-xs">
            <div className="grid-cell">
              <div className="label-mono mb-1">ШКАЛА</div>
              <div className="mono">{score.scoring_profile_name} <span className="text-foreground/50">({score.scoring_profile_version})</span></div>
            </div>
            <div className="grid-cell">
              <div className="label-mono mb-1">AI-ОБОГАЩЕНИЕ</div>
              <div data-testid="ai-status-label">{AI_STATUS_LABELS[score.ai_extraction_status] || "—"}</div>
            </div>
            <div className="grid-cell">
              <div className="label-mono mb-1">УВЕРЕННОСТЬ</div>
              <div data-testid="confidence-label">{CONFIDENCE_LABELS[score.confidence] || "—"}</div>
            </div>
          </div>
          <div className="text-xs text-foreground/60 mono mt-3">
            Профиль: {score.profile_label}
          </div>
        </div>
      ) : (
        <p className="label-mono">Скоринг ещё не запущен</p>
      )}

      {/* Components breakdown */}
      {score && (
        <div>
          <div className="label-mono mb-4">КОМПОНЕНТЫ</div>
          <div className="grid md:grid-cols-3 gap-4">
            {["investor", "corporate", "institutional"].map((idx) => (
              <div key={idx} className="grid-cell">
                <div className="label-mono mb-3">{INDEX_LABELS[idx]}</div>
                <div className="space-y-2">
                  {Object.entries(score.components[idx] || {}).map(([cname, cdata]) => (
                    <div key={cname} className="flex items-baseline justify-between gap-2 text-sm">
                      <div className="flex-1 truncate">
                        <span className={cdata.answered ? "" : "text-foreground/40"}>{COMPONENT_LABELS[cname] || cname}</span>
                        {cdata.source === "deck" && (
                          <span className="ml-1 text-xs text-foreground/50">· из дека</span>
                        )}
                      </div>
                      <div className="mono text-xs text-foreground/70 tabular-nums">
                        {cdata.average_5}/5 · w{cdata.weight}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pitch Deck */}
      <div>
        <div className="label-mono mb-3">ПРЕЗЕНТАЦИЯ</div>
        {deck ? (
          <div className="space-y-3">
            <div className="grid-cell flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[260px]">
                <div className="text-base font-medium">{deck.filename}</div>
                <div className="text-xs text-foreground/60 mono mt-1">
                  {deck.format?.toUpperCase()} · {deck.pages} стр · {Math.round((deck.size_bytes || 0) / 1024)} КБ · загружено {fmtDate(deck.uploaded_at)}
                </div>
                <div className="text-xs text-foreground/60 mono mt-1">
                  AI-анализ: <span className="text-foreground" data-testid="deck-ai-status">{AI_STATUS_LABELS[deck.extraction?.status] || "—"}</span>
                  {typeof deckParsed.deck_quality_score === "number" && (
                    <> · качество дека: <span className="text-foreground">{deckParsed.deck_quality_score}/5</span></>
                  )}
                </div>
              </div>
              <div className="flex gap-2 print:hidden">
                <button className="btn-secondary" onClick={openDeck} data-testid="deck-download-btn">
                  <Download size={14} /> Скачать
                </button>
                <button className="btn-primary" disabled={busy} onClick={reanalyzeDeck} data-testid="deck-reanalyze-btn">
                  <RefreshCw size={14} /> {busy ? "Анализ…" : "Повторный анализ"}
                </button>
              </div>
            </div>
            {deck.extraction?.status === "ok" && (
              <div className="grid md:grid-cols-2 gap-3">
                {DECK_FIELD_LABELS.map(([k, label]) => (
                  <div key={k} className="grid-cell text-sm">
                    <div className="label-mono mb-1">{label}</div>
                    <div className="text-foreground/85 leading-relaxed whitespace-pre-wrap">
                      {deckParsed[k] || <span className="text-foreground/40">не указано</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-foreground/60">Презентация не загружена.</p>
        )}
      </div>

      {/* Strengths / Gaps */}
      {recommendation && ((recommendation.strengths_json || []).length > 0 || (recommendation.gaps_json || []).length > 0) && (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="label-mono mb-3">СИЛЬНЫЕ СТОРОНЫ</div>
            <ul className="space-y-2">
              {(recommendation.strengths_json || []).length === 0 && (
                <li className="text-sm text-foreground/50">не выявлены</li>
              )}
              {(recommendation.strengths_json || []).map((s, i) => (
                <li key={i} className="grid-cell text-sm leading-relaxed">{s}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label-mono mb-3">ЗОНЫ РОСТА</div>
            <ul className="space-y-2">
              {(recommendation.gaps_json || []).length === 0 && (
                <li className="text-sm text-foreground/50">не выявлены</li>
              )}
              {(recommendation.gaps_json || []).map((s, i) => (
                <li key={i} className="grid-cell text-sm leading-relaxed">{s}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendation && (recommendation.advice_json || []).length > 0 && (
        <div>
          <div className="label-mono mb-3">РЕКОМЕНДАЦИИ (90 ДНЕЙ)</div>
          <ol className="space-y-3" data-testid="advice-list">
            {(recommendation.advice_json || []).map((r, i) => (
              <li key={r.rule_id || i} className="grid-cell">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="mono text-xs text-foreground/50">#{String(i + 1).padStart(2, "0")}</span>
                  <span className="label-mono">[{r.priority?.toUpperCase()}] · {INDEX_LABELS[r.index] || r.index}</span>
                </div>
                <div className="mt-2 text-base font-medium">{r.title}</div>
                {r.why && <div className="mt-2 text-sm text-foreground/75 leading-relaxed">{r.why}</div>}
                {r.action_90_days && (
                  <div className="mt-2 text-sm text-foreground/85 leading-relaxed">
                    <span className="label-mono mr-1">90 дней:</span>{r.action_90_days}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Answers */}
      <div>
        <div className="label-mono mb-3">ОТВЕТЫ НА АНКЕТУ</div>
        {sectionedAnswers.length === 0 ? (
          <p className="text-sm text-foreground/60">Загрузка вопросов…</p>
        ) : (
          <div className="space-y-6">
            {sectionedAnswers.map(({ section, items }) => (
              <div key={section}>
                <div className="text-sm font-medium text-foreground/70 mb-2">{section}</div>
                <div className="border border-black/10">
                  {items.map(({ q, value, label }, idx) => (
                    <div
                      key={q.id}
                      className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4 px-4 py-3 text-sm ${idx === items.length - 1 ? "" : "border-b border-black/10"}`}
                    >
                      <div className="text-foreground/85">{q.question || q.title || q.id}</div>
                      <div>
                        {label === null || label === undefined || label === "" ? (
                          <span className="text-foreground/40">— нет ответа —</span>
                        ) : (
                          <span className="text-foreground whitespace-pre-wrap">{label}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recompute panel */}
      <div className="grid-cell print:hidden">
        <div className="label-mono mb-3">ПЕРЕСЧИТАТЬ ПОД ДРУГОЙ ШКАЛОЙ</div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            className="field-input max-w-md"
            value={recomp.profile_id}
            onChange={(e) => setRecomp({ profile_id: e.target.value })}
            data-testid="recompute-profile-select"
          >
            <option value="">— Авто-выбор по триггерам —</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.version})</option>
            ))}
          </select>
          <button className="btn-primary" disabled={busy} onClick={recompute} data-testid="recompute-btn">
            {busy ? "Считаем…" : "Пересчитать"}
          </button>
        </div>
      </div>

      {/* EMD Cloud export */}
      <div className="grid-cell print:hidden" data-testid="emd-export-block">
        <div className="label-mono mb-2">EMD CLOUD · Hop Agency</div>
        <p className="text-sm text-foreground/70">
          Выгрузить карточку стартапа в основную коллекцию Hop Agency. Каждая выгрузка создаёт новую строку в EMD — дубликаты разбираются на стороне EMD.
        </p>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button className="btn-primary" disabled={emdStatus === "busy"} onClick={exportToEmd} data-testid="emd-export-btn">
            {emdStatus === "busy" ? "Выгружаем…" : "Выгрузить в EMD Cloud"}
          </button>
          {emdStatus?.ok && <span className="label-mono text-foreground/70" data-testid="emd-export-ok">✓ ВЫГРУЖЕНО</span>}
          {emdStatus?.error && <span className="label-mono text-destructive" data-testid="emd-export-error">ОШИБКА: {emdStatus.error}</span>}
        </div>
      </div>

      {/* Verification alerts — cross-check AI facts vs founder's closed answers */}
      {verification && (verification.result_json?.alerts?.length > 0 || verification.status !== "ok") && (
        <div className="grid-cell print:break-inside-avoid" data-testid="verification-block">
          <div className="label-mono mb-3">
            AI-ВЕРИФИКАЦИЯ · проверка на противоречия
          </div>
          {verification.status !== "ok" && (
            <div className="text-xs text-foreground/60 mb-2">
              Статус: <span className="mono">{verification.status}</span>
            </div>
          )}
          {verification.result_json?.summary && (
            <div className="text-sm text-foreground/80 mb-3" data-testid="verification-summary">
              {verification.result_json.summary}
            </div>
          )}
          {verification.result_json?.alerts?.length > 0 && (
            <div className="space-y-2">
              {verification.result_json.alerts.map((a, i) => {
                const border =
                  a.severity === "high"
                    ? "border-red-500/60 bg-red-50/50"
                    : a.severity === "medium"
                    ? "border-amber-500/60 bg-amber-50/40"
                    : "border-foreground/20 bg-secondary/40";
                return (
                  <div
                    key={i}
                    className={`border-l-4 pl-3 py-2 pr-3 text-sm ${border}`}
                    data-testid={`verification-alert-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="label-mono text-[10px] uppercase">
                        {a.severity}
                      </span>
                      <span className="mono text-xs text-foreground/60">
                        {a.field}
                      </span>
                    </div>
                    <div className="mb-1">{a.description}</div>
                    <div className="text-xs text-foreground/60 space-y-0.5">
                      <div>
                        <span className="label-mono">дек:</span> {a.deck_says}
                      </div>
                      <div>
                        <span className="label-mono">основатель:</span> {a.founder_says}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tech details */}
      <details className="grid-cell print:hidden">
        <summary className="cursor-pointer label-mono">ТЕХНИЧЕСКИЕ ДАННЫЕ</summary>
        <div className="mt-4 space-y-4 text-xs">
          {extraction && (
            <div>
              <div className="label-mono mb-1">AI-EXTRACTION открытых ответов</div>
              <div>статус: <span className="mono">{extraction.status}</span> · модель: <span className="mono">{extraction.model_name}</span></div>
              <pre className="bg-secondary p-3 mt-2 overflow-auto whitespace-pre-wrap">{JSON.stringify(extraction.parsed_json, null, 2)}</pre>
            </div>
          )}
          {verification && (
            <div>
              <div className="label-mono mb-1">AI-VERIFICATION (второй проход)</div>
              <div>статус: <span className="mono">{verification.status}</span> · модель: <span className="mono">{verification.model_name}</span> · алертов: <span className="mono">{verification.result_json?.alerts?.length ?? 0}</span></div>
              <pre className="bg-secondary p-3 mt-2 overflow-auto whitespace-pre-wrap">{JSON.stringify(verification.result_json, null, 2)}</pre>
            </div>
          )}
          {deck?.extraction && (
            <div>
              <div className="label-mono mb-1">AI-EXTRACTION дека (parsed_json)</div>
              <pre className="bg-secondary p-3 mt-2 overflow-auto whitespace-pre-wrap">{JSON.stringify(deckParsed, null, 2)}</pre>
            </div>
          )}
          <div>
            <div className="label-mono mb-1">RAW ANSWERS</div>
            <pre className="bg-secondary p-3 mt-2 overflow-auto whitespace-pre-wrap" data-testid="raw-answers-pre">
              {JSON.stringify(submission.raw_answers_json, null, 2)}
            </pre>
          </div>
        </div>
      </details>
    </div>
  );
}

function ContactRow({ icon, label, children }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-foreground/50 inline-flex w-4">{icon}</span>
      <span className="text-foreground/55 mono text-xs uppercase tracking-wider w-32">{label}</span>
      <span className="flex-1">{children}</span>
    </div>
  );
}

function PublicShareBlock({ shareToken }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/share/${shareToken}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for older browsers / non-secure origins
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <div
      className="border border-black/10 bg-secondary/30 px-4 py-3 flex flex-wrap items-center gap-3 print:hidden"
      data-testid="public-share-block"
    >
      <span className="label-mono text-xs text-foreground/60 uppercase tracking-wider">
        Публичная ссылка основателя
      </span>
      <code
        className="mono text-xs text-foreground/80 flex-1 min-w-0 truncate"
        data-testid="public-share-url"
        title={url}
      >
        {url}
      </code>
      <button
        onClick={copy}
        className="btn-ghost text-xs"
        data-testid="public-share-copy-btn"
        title="Скопировать ссылку в буфер обмена"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Скопировано" : "Копировать"}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-secondary text-xs"
        data-testid="public-share-open-btn"
        title="Открыть публичную страницу результата в новой вкладке"
      >
        <ExternalLink size={12} /> Открыть
      </a>
    </div>
  );
}
